import {
  useQuery,
  useMutation,
  useQueryClient,
  useInfiniteQuery,
} from '@tanstack/react-query';
import {
  Recipe,
  RecipeListParams,
  PaginatedResponse,
} from '@coffeeplexity/shared';
import { apiClient } from '../client/http-client';
import { getSyncEngine } from '@coffeeplexity/shared';
import type { MutationTask } from '@coffeeplexity/shared';
import type {
  SavedSearchCreate,
  SavedSearchUpdate,
} from '@coffeeplexity/shared';
import { queryKeys } from '../utils/query-keys';

export function useRecipes(params?: RecipeListParams) {
  return useQuery({
    queryKey: queryKeys.recipes.list(params),
    queryFn: () => apiClient.getRecipes(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function useInfiniteRecipes(params?: RecipeListParams) {
  return useInfiniteQuery({
    queryKey: queryKeys.recipes.infinite(params),
    queryFn: ({ pageParam = 1 }) =>
      apiClient.getRecipes({ ...params, page: pageParam }),
    getNextPageParam: (lastPage: PaginatedResponse<Recipe>) =>
      lastPage.has_next ? lastPage.page + 1 : undefined,
    initialPageParam: 1,
    staleTime: 5 * 60 * 1000,
  });
}

export function useRecipe(id: string) {
  return useQuery({
    queryKey: queryKeys.recipes.detail(id),
    queryFn: () => apiClient.getRecipe(id),
    enabled: !!id,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
}

export function useCreateRecipe() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (recipe: Partial<Recipe>) => apiClient.createRecipe(recipe),
    onSuccess: newRecipe => {
      // Invalidate recipe lists
      queryClient.invalidateQueries({ queryKey: queryKeys.recipes.lists() });
      // Add the new recipe to cache
      queryClient.setQueryData(
        queryKeys.recipes.detail(newRecipe.id),
        newRecipe
      );
    },
  });
}

export function useUpdateRecipe() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, recipe }: { id: string; recipe: Partial<Recipe> }) =>
      apiClient.updateRecipe(id, recipe),
    onSuccess: updatedRecipe => {
      // Update the recipe in cache
      queryClient.setQueryData(
        queryKeys.recipes.detail(updatedRecipe.id),
        updatedRecipe
      );
      // Invalidate recipe lists to reflect changes
      queryClient.invalidateQueries({ queryKey: queryKeys.recipes.lists() });
    },
  });
}

export function useDeleteRecipe() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => apiClient.deleteRecipe(id),
    onSuccess: (_, deletedId) => {
      // Remove from cache
      queryClient.removeQueries({
        queryKey: queryKeys.recipes.detail(deletedId),
      });
      // Invalidate lists
      queryClient.invalidateQueries({ queryKey: queryKeys.recipes.lists() });
    },
  });
}

export function useLikeRecipe() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => apiClient.likeRecipe(id),
    onMutate: async recipeId => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({
        queryKey: queryKeys.recipes.detail(recipeId),
      });

      // Snapshot previous value
      const previousRecipe = queryClient.getQueryData<Recipe>(
        queryKeys.recipes.detail(recipeId)
      );

      // Optimistically update
      if (previousRecipe) {
        queryClient.setQueryData<Recipe>(queryKeys.recipes.detail(recipeId), {
          ...previousRecipe,
          favorite_count: previousRecipe.favorite_count + 1,
          likes_count: (previousRecipe.likes_count || 0) + 1,
        });
      }

      return { previousRecipe };
    },
    onError: async (_, recipeId, context) => {
      if (typeof navigator !== 'undefined' && !navigator.onLine) {
        const engine = getSyncEngine();
        const task: MutationTask = {
          id: `${Date.now()}-like-${recipeId}`,
          type: 'likeRecipe',
          payload: { id: recipeId },
          createdAt: Date.now(),
          entityId: recipeId,
          entityType: 'recipe',
          clientVersion: new Date().toISOString(),
        };
        await engine?.enqueue(task);
      }
      // Rollback on error
      if (context?.previousRecipe) {
        queryClient.setQueryData(
          queryKeys.recipes.detail(recipeId),
          context.previousRecipe
        );
      }
    },
    onSettled: (_, __, recipeId) => {
      // Refetch to ensure consistency
      queryClient.invalidateQueries({
        queryKey: queryKeys.recipes.detail(recipeId),
      });
      // Also invalidate interaction status
      queryClient.invalidateQueries({
        queryKey: queryKeys.recipes.interactions(recipeId),
      });
      // And bulk interactions (for list views)
      queryClient.invalidateQueries({
        queryKey: ['recipes', 'interactions', 'bulk'],
      });
    },
  });
}

export function useUnlikeRecipe() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => apiClient.unlikeRecipe(id),
    onMutate: async recipeId => {
      await queryClient.cancelQueries({
        queryKey: queryKeys.recipes.detail(recipeId),
      });

      const previousRecipe = queryClient.getQueryData<Recipe>(
        queryKeys.recipes.detail(recipeId)
      );

      if (previousRecipe) {
        queryClient.setQueryData<Recipe>(queryKeys.recipes.detail(recipeId), {
          ...previousRecipe,
          favorite_count: Math.max(0, previousRecipe.favorite_count - 1),
          likes_count: Math.max(0, (previousRecipe.likes_count || 0) - 1),
        });
      }

      return { previousRecipe };
    },
    onError: async (_, recipeId, context) => {
      if (typeof navigator !== 'undefined' && !navigator.onLine) {
        const engine = getSyncEngine();
        const task: MutationTask = {
          id: `${Date.now()}-unlike-${recipeId}`,
          type: 'unlikeRecipe',
          payload: { id: recipeId },
          createdAt: Date.now(),
          entityId: recipeId,
          entityType: 'recipe',
          clientVersion: new Date().toISOString(),
        };
        await engine?.enqueue(task);
      }
      if (context?.previousRecipe) {
        queryClient.setQueryData(
          queryKeys.recipes.detail(recipeId),
          context.previousRecipe
        );
      }
    },
    onSettled: (_, __, recipeId) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.recipes.detail(recipeId),
      });
      // Also invalidate interaction status
      queryClient.invalidateQueries({
        queryKey: queryKeys.recipes.interactions(recipeId),
      });
      // And bulk interactions (for list views)
      queryClient.invalidateQueries({
        queryKey: ['recipes', 'interactions', 'bulk'],
      });
    },
  });
}

export function useBookmarkRecipe() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => apiClient.bookmarkRecipe(id),
    onMutate: async recipeId => {
      await queryClient.cancelQueries({
        queryKey: queryKeys.recipes.detail(recipeId),
      });
      const previousRecipe = queryClient.getQueryData<Recipe>(
        queryKeys.recipes.detail(recipeId)
      );
      if (previousRecipe) {
        queryClient.setQueryData<Recipe>(queryKeys.recipes.detail(recipeId), {
          ...previousRecipe,
          bookmarks_count: (previousRecipe.bookmarks_count || 0) + 1,
        });
      }
      return { previousRecipe };
    },
    onError: async (_, recipeId, context) => {
      if (typeof navigator !== 'undefined' && !navigator.onLine) {
        const engine = getSyncEngine();
        await engine?.enqueue({
          id: `${Date.now()}-bookmark-${recipeId}`,
          type: 'bookmarkRecipe',
          payload: { id: recipeId },
          createdAt: Date.now(),
          entityId: recipeId,
          entityType: 'recipe',
          clientVersion: new Date().toISOString(),
        });
      }
      if (context?.previousRecipe) {
        queryClient.setQueryData(
          queryKeys.recipes.detail(recipeId),
          context.previousRecipe
        );
      }
    },
    onSettled: (_, __, recipeId) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.recipes.bookmarked(),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.recipes.detail(recipeId),
      });
      // Also invalidate interaction status
      queryClient.invalidateQueries({
        queryKey: queryKeys.recipes.interactions(recipeId),
      });
      // And bulk interactions (for list views)
      queryClient.invalidateQueries({
        queryKey: ['recipes', 'interactions', 'bulk'],
      });
    },
  });
}

export function useUnbookmarkRecipe() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => apiClient.unbookmarkRecipe(id),
    onMutate: async recipeId => {
      await queryClient.cancelQueries({
        queryKey: queryKeys.recipes.detail(recipeId),
      });
      const previousRecipe = queryClient.getQueryData<Recipe>(
        queryKeys.recipes.detail(recipeId)
      );
      if (previousRecipe) {
        queryClient.setQueryData<Recipe>(queryKeys.recipes.detail(recipeId), {
          ...previousRecipe,
          bookmarks_count: Math.max(
            0,
            (previousRecipe.bookmarks_count || 0) - 1
          ),
        });
      }
      return { previousRecipe };
    },
    onError: async (_, recipeId, context) => {
      if (typeof navigator !== 'undefined' && !navigator.onLine) {
        const engine = getSyncEngine();
        await engine?.enqueue({
          id: `${Date.now()}-unbookmark-${recipeId}`,
          type: 'unbookmarkRecipe',
          payload: { id: recipeId },
          createdAt: Date.now(),
          entityId: recipeId,
          entityType: 'recipe',
          clientVersion: new Date().toISOString(),
        });
      }
      if (context?.previousRecipe) {
        queryClient.setQueryData(
          queryKeys.recipes.detail(recipeId),
          context.previousRecipe
        );
      }
    },
    onSettled: (_, __, recipeId) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.recipes.bookmarked(),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.recipes.detail(recipeId),
      });
      // Also invalidate interaction status
      queryClient.invalidateQueries({
        queryKey: queryKeys.recipes.interactions(recipeId),
      });
      // And bulk interactions (for list views)
      queryClient.invalidateQueries({
        queryKey: ['recipes', 'interactions', 'bulk'],
      });
    },
  });
}

// Custom hook for recipe interactions
export function useRecipeInteractions(recipeId: string) {
  const likeRecipe = useLikeRecipe();
  const unlikeRecipe = useUnlikeRecipe();
  const bookmarkRecipe = useBookmarkRecipe();
  const unbookmarkRecipe = useUnbookmarkRecipe();

  return {
    like: () => likeRecipe.mutate(recipeId),
    unlike: () => unlikeRecipe.mutate(recipeId),
    bookmark: () => bookmarkRecipe.mutate(recipeId),
    unbookmark: () => unbookmarkRecipe.mutate(recipeId),
    isLiking: likeRecipe.isPending,
    isUnliking: unlikeRecipe.isPending,
    isBookmarking: bookmarkRecipe.isPending,
    isUnbookmarking: unbookmarkRecipe.isPending,
  };
}

// Hook for trending recipes
export function useTrendingRecipes() {
  return useRecipes({
    sort_by: 'view_count',
    sort_order: 'desc',
    limit: 10,
  });
}

// Hook for featured recipes
export function useFeaturedRecipes() {
  return useRecipes({
    filters: { is_featured: true },
    limit: 6,
  });
}

export function useSearchSuggestions(query: string, type?: string) {
  return useQuery({
    queryKey: queryKeys.search.suggestions(query, type),
    queryFn: () => apiClient.searchSuggestions(query, type),
    enabled: query.trim().length > 0,
    staleTime: 60 * 1000,
  });
}

export function useSearchAutocomplete(query: string, type?: string) {
  return useQuery({
    queryKey: queryKeys.search.autocomplete(query, type),
    queryFn: () => apiClient.searchAutocomplete(query, type),
    enabled: query.trim().length > 0,
    staleTime: 60 * 1000,
  });
}

// Saved searches hooks
export function useSavedSearches(params?: { page?: number; size?: number }) {
  return useQuery({
    queryKey: queryKeys.search.saved.list(params),
    queryFn: () => apiClient.listSavedSearches(params),
    staleTime: 60 * 1000,
  });
}

export function useCreateSavedSearch() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: SavedSearchCreate) =>
      apiClient.createSavedSearch(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.search.saved.all });
    },
  });
}

export function useUpdateSavedSearch() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: SavedSearchUpdate }) =>
      apiClient.updateSavedSearch(id, payload),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.search.saved.detail(variables.id),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.search.saved.all });
    },
  });
}

export function useDeleteSavedSearch() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.deleteSavedSearch(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.search.saved.all });
    },
  });
}

// Export publish/unpublish hooks
export { usePublishRecipe, useUnpublishRecipe } from './usePublishRecipe';
